import {
  Button,
  Checkbox,
  CheckboxOnChangeData,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Label,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Textarea,
  TextareaOnChangeData,
} from "@fluentui/react-components";
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PublishAsGeneratedPdfFieldUniqueName } from "../../constants/Fields";
import {
  useSaveAndPublishToolDraft,
  useValidateDraftFile,
  usePublishToolDraft,
} from "../../hooks/useTools";
import { FieldValue } from "../../models/Field";
import { ClientRoutes } from "../../models/ClientRoutes";
import { PublishToolRequest, ToolTypes } from "../../models/Tool";
import { ButtonWithLoading } from "../ButtonWithLoading";

const useStyles = makeStyles({
  dialogSurface: {
    width: "500px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
  },
  commentTextArea: {
    minHeight: "120px",
    marginTop: "8px",
  },
  commentLabel: {
    marginTop: "16px",
  },
  messageBar: {
    whiteSpace: "normal",
    wordBreak: "break-word",
    marginBottom: "16px",
  },
});

interface ToolPendingUpdate {
  title: string;
  toolType?: string;
  linkUrl?: string;
  fileName?: string;
  adminDisplayName?: string;
  fieldsWithValues: Record<string, FieldValue>;
  file?: File;
}

interface ToolPublishDialogProps {
  versionId: string;
  toolId?: string;
  disabled?: boolean;
  tooltype?: string;
  onPublishStart?: () => void;
  onPublishFailed?: () => void;
  /** The current form data to save, in the same request that publishes the draft. */
  pendingUpdate: ToolPendingUpdate;
  /** Increment this value to open the dialog programmatically, e.g. after a combined save-and-publish action. */
  openSignal?: number;
  onlyPublish?: boolean;
}

const ToolPublishDialog: React.FC<ToolPublishDialogProps> = ({
  versionId,
  toolId,
  disabled,
  tooltype,
  onPublishStart,
  onPublishFailed,
  pendingUpdate,
  openSignal,
  onlyPublish,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const classes = useStyles();

  const { mutateAsync: saveAndPublishTool, isPending: isSaveAndPublishing } =
    useSaveAndPublishToolDraft(versionId, toolId);

  const isDocumentTool = tooltype === ToolTypes.Document;
  const hasPendingFile = Boolean(pendingUpdate.file);
  const { mutateAsync: validateFile, isPending: isValidating } =
    useValidateDraftFile();

  const { mutateAsync: publishTool, isPending: isPublishing } =
    usePublishToolDraft(versionId, toolId);

  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState<string>("");
  const [hiddenComment, setHiddenComment] = useState<boolean>(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [fileValid, setFileValid] = useState<boolean | null>(null);

  // Open the dialog whenever the parent requests it via `openSignal`,
  // e.g. after a combined save-and-publish action.
  const [lastOpenSignal, setLastOpenSignal] = useState(openSignal);
  if (openSignal !== lastOpenSignal) {
    setLastOpenSignal(openSignal);
    if (openSignal) {
      setOpen(true);
    }
  }

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    if (open && isDocumentTool && !hasPendingFile) {
      setFileValid(null);
      setPublishError(null);
      validateFile({ versionId }).then((isValid) => {
        setFileValid(isValid);
        if (!isValid) {
          setPublishError(t("publishFileValidationError"));
        }
      });
    }
    /* eslint-enable react-hooks/set-state-in-effect */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasPendingFile]);

  const onCommentChange = (
    _ev: ChangeEvent<HTMLTextAreaElement>,
    data: TextareaOnChangeData
  ) => setComment(data.value);

  const onHiddenChange = (
    _ev: ChangeEvent<HTMLInputElement>,
    data: CheckboxOnChangeData
  ) => setHiddenComment(data.checked as boolean);

  const onPublish = async () => {
    if (!comment) return;
    setPublishError(null);

    const publishToolRequest: PublishToolRequest = {
      versionId,
      comment,
      hiddenComment,
    };

    onPublishStart?.();

    try {
      await publishTool(publishToolRequest);
      setOpen(false);
      if (tooltype === ToolTypes.Document) {
        navigate(ClientRoutes.Documents);
      } else if (tooltype === ToolTypes.Link) {
        navigate(ClientRoutes.Links);
      }
    } catch (error) {
      onPublishFailed?.();
      const apiError = error as { status?: number };
      if (apiError?.status === 400) {
        setFileValid(false);
      }
      setPublishError(t("publishError"));
    }
  };

  const onSaveAndPublish = async () => {
    if (!comment) return;
    setPublishError(null);

    const formDataToSend = new FormData();
    formDataToSend.append("VersionId", versionId);
    formDataToSend.append("Title", pendingUpdate.title);
    if (pendingUpdate.toolType) {
      formDataToSend.append("ToolType", pendingUpdate.toolType);
    }
    if (pendingUpdate.file) {
      formDataToSend.append("NewFile", pendingUpdate.file);
    }
    if (pendingUpdate.fileName) {
      formDataToSend.append("FileName", pendingUpdate.fileName);
    }
    if (pendingUpdate.linkUrl) {
      formDataToSend.append("LinkUrl", pendingUpdate.linkUrl);
    }
    formDataToSend.append(
      "AdminDisplayName",
      pendingUpdate.adminDisplayName || ""
    );
    formDataToSend.append(
      "FieldsWithValues",
      JSON.stringify(pendingUpdate.fieldsWithValues)
    );
    formDataToSend.append("Comment", comment);
    formDataToSend.append("HiddenComment", String(hiddenComment));
    const publishAsGeneratedPdf =
      pendingUpdate.fieldsWithValues[PublishAsGeneratedPdfFieldUniqueName] ===
      true;
    formDataToSend.append("AsPdf", String(publishAsGeneratedPdf));

    onPublishStart?.();

    try {
      await saveAndPublishTool(formDataToSend);
      setOpen(false);
      if (tooltype === ToolTypes.Document) {
        navigate(ClientRoutes.Documents);
      } else if (tooltype === ToolTypes.Link) {
        navigate(ClientRoutes.Links);
      }
    } catch (error) {
      onPublishFailed?.();
      const apiError = error as { status?: number };
      if (apiError?.status === 400) {
        setFileValid(false);
      }
      setPublishError(t("publishError"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
      <DialogTrigger disableButtonEnhancement>
        <Button disabled={disabled} appearance="primary">
          {t("publish")}
        </Button>
      </DialogTrigger>

      <DialogSurface className={classes.dialogSurface}>
        <DialogBody>
          <DialogTitle>{t("publishDraftDialogTitle")}</DialogTitle>
          <DialogContent>
            <div className={classes.content}>
              {publishError && (
                <MessageBar intent="error" className={classes.messageBar}>
                  <MessageBarBody>{publishError}</MessageBarBody>
                </MessageBar>
              )}
              <div>{t("publishDraftDescription")}</div>
              <Label className={classes.commentLabel}>
                {t("commentLabel")}
              </Label>
              <Textarea
                value={comment}
                onChange={onCommentChange}
                textarea={{ className: classes.commentTextArea }}
              />
              <Checkbox
                label={t("hideRevisionCheckboxLabel")}
                checked={hiddenComment}
                onChange={onHiddenChange}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpen(false)}
              appearance="secondary"
              disabled={isSaveAndPublishing || isPublishing}
            >
              {t("close")}
            </Button>
            <ButtonWithLoading
              onClick={onlyPublish ? onPublish : onSaveAndPublish}
              disabled={
                !comment ||
                isValidating ||
                (isDocumentTool && !hasPendingFile && fileValid !== true)
              }
              isLoading={isSaveAndPublishing || isValidating || isPublishing}
              text={t("publish")}
            />
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ToolPublishDialog;
